import {useCallback, useEffect, useState} from 'react';

import {bankService} from '@/services/bankService';
import type {AfriexBank} from '@/types';

import type {BankFilterState, ProcessorFilterValue} from './types';
import {ANY_PROCESSOR, NOT_MAPPED, PAGE_SIZE} from './types';

const SEARCH_DEBOUNCE_MS = 300;

const toServerFilters = ({
  countryCodes,
  processorFilter,
  search,
}: BankFilterState) => ({
  countryCodes,
  search,
  unmapped: processorFilter === NOT_MAPPED,
  processor:
    processorFilter === ANY_PROCESSOR || processorFilter === NOT_MAPPED
      ? undefined
      : processorFilter,
});

type LoadedPage = {
  key: string;
  list: AfriexBank[];
  total: number;
  error: unknown;
};

/**
 * Processors -> Banks. Nothing is fetched until a country is picked: the
 * directory holds ~100k SWIFT entries (74k for CN alone), so an unscoped
 * browse is never what anyone wants.
 *
 * Loading and page reset are derived from which request last landed rather
 * than set inside the fetch effect: `requestKey` names the filters + page on
 * screen, and the list is "loading" until a response for that key arrives.
 */
const useBankList = () => {
  const [filters, setFilters] = useState<BankFilterState>({
    countryCodes: [],
    processorFilter: ANY_PROCESSOR,
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageRequest, setPageRequest] = useState({filterKey: '', page: 0});
  const [loaded, setLoaded] = useState<LoadedPage>({
    key: '',
    list: [],
    total: 0,
    error: null,
  });

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  const {countryCodes, processorFilter} = filters;
  const hasCountries = countryCodes.length > 0;
  const filterKey = [
    countryCodes.join(','),
    processorFilter,
    debouncedSearch,
  ].join('|');
  // Any filter change starts again from the first page.
  const page = pageRequest.filterKey === filterKey ? pageRequest.page : 0;
  const requestKey = `${filterKey}#${page}`;

  useEffect(() => {
    if (!countryCodes.length) {
      return undefined;
    }
    // A response for filters that have since changed must not land.
    let isCurrent = true;
    bankService
      .getBankList({
        ...toServerFilters({
          countryCodes,
          processorFilter,
          search: debouncedSearch,
        }),
        page,
        limit: PAGE_SIZE,
      })
      .then(
        (response) => {
          if (isCurrent) {
            setLoaded({
              key: requestKey,
              list: response.data ?? [],
              total: response.total ?? 0,
              error: null,
            });
          }
        },
        (error: unknown) => {
          if (isCurrent) {
            setLoaded({key: requestKey, list: [], total: 0, error});
          }
        },
      );
    return () => {
      isCurrent = false;
    };
  }, [countryCodes, processorFilter, debouncedSearch, page, requestKey]);

  // Promise-returning to match Pagination's `onPageChange`; the fetch itself
  // runs in the effect above once the page request lands.
  const onPageChange = useCallback(
    ({page: nextPage}: {page?: number}): Promise<void> => {
      setPageRequest({filterKey, page: Number(nextPage ?? 0)});
      return Promise.resolve();
    },
    [filterKey],
  );

  const setCountryCodes = (codes: string[]) =>
    setFilters((current) => ({...current, countryCodes: codes}));
  const setProcessorFilter = (value: ProcessorFilterValue) =>
    setFilters((current) => ({...current, processorFilter: value}));
  const setSearch = (value: string) =>
    setFilters((current) => ({...current, search: value}));

  const isCurrentPage = hasCountries && loaded.key === requestKey;
  // Keep the total while another page of the same filters loads, so the pager
  // stays mounted on its current page.
  const isSameFilters = hasCountries && loaded.key.startsWith(`${filterKey}#`);

  return {
    filters,
    setCountryCodes,
    setProcessorFilter,
    setSearch,
    list: isCurrentPage ? loaded.list : [],
    total: isSameFilters ? loaded.total : 0,
    page,
    // Changes with every filter set; the page keys Pagination on it so the
    // pager's own page number starts again at 1.
    filterKey,
    isLoading: hasCountries && !isCurrentPage,
    error: isCurrentPage ? loaded.error : null,
    onPageChange,
  };
};

export default useBankList;
